import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  confirmDialog,
  CustomCollapseCard,
  CustomButton,
  CustomImage,
  CustomPagination,
  successDialog,
  Text,
  useText,
  warningDialog,
} from '@src/components';
import { useLang } from '@src/context';

import DownloadModal from './DownloadModal';
import PaymentModal from './PaymentModal';
import OrderSummaryCardModal from './OrderSummaryCardModal';
import OrderSummaryBalanceModal from './OrderSummaryBalanceModal';
import OrderFilter from './OrderFilter';

import {
  orderDelete,
  orderGetPageList,
  orderInfosGetList,
  setSelectedOrder,
} from '@src/store/slice/orderSlice';
import { getOrderLimitDate, resetOrderLimits } from '@src/store/slice/orderLimitsSlice';
import { prepareLoadBalance } from '@src/store/slice/balanceUploadSlice';
import { getCustomerVouList } from '@src/store/slice/customerSlice';

import reportsServices from '@src/services/reports.services';

import dayjs from '@src/utils/day';
import { defaultDateFormat } from '@src/utils/keys';
import { handleAuthStatus, previewDownload } from '@src/utils/utils';

import { Collapse } from 'antd';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import iconDown from '@src/assets/icons/icon-down.svg';
import iconUp from '@src/assets/icons/icon-up.svg';
import '@src/styles/myOrders/orderList.scss';
import { FilterOrderTypeValue } from '@src/store/enums';

const { Panel } = Collapse;

function getExpandIcon(setActiveCollapse, switchIcon) {
  return ({ isActive }) => {
    setActiveCollapse(isActive);
    return (
      <div style={{ border: '2px solid #586377', borderRadius: '24px' }}>
        {isActive ? (
          <CustomImage className="open-close" src={!switchIcon ? iconUp : iconDown} />
        ) : (
          <CustomImage className="open-close" src={!switchIcon ? iconDown : iconUp} />
        )}
      </div>
    );
  };
}

const OrderList = () => {
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });

  const dispatch = useDispatch();
  const history = useHistory();

  const { list, tableProperty, filterObject } = useSelector((state) => state?.order);
  const { orderLimitDate } = useSelector((state) => state?.orderLimits);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { customerVou } = useSelector((state) => state?.customer);
  const { userAuthorizationStatus } = useSelector((state) => state?.user);

  const [activeCollapse, setActiveCollapse] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [orderSummaryCardModalVisible, setOrderSummaryCardModalVisible] = useState(false);
  const [orderSummaryBalanceModalVisible, setOrderSummaryBalanceModalVisible] = useState(false);

  const detailText = useText('detail');
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const { dictionary } = useLang();

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await dispatch(orderGetPageList({ ...filterObject, pageNumber, pageSize }));
      await dispatch(resetOrderLimits());
    },
    [dispatch, filterObject],
  );

  useEffect(() => {
    dispatch(
      getCustomerVouList({
        customerId: selectedCustomer?.customerId,
        vouId: 1,
      }),
    );
  }, [dispatch, selectedCustomer]);

  const handleDetail = (record) => {
    dispatch(setSelectedOrder(record));
    record.orderTypeId === 9
      ? setOrderSummaryCardModalVisible(true)
      : setOrderSummaryBalanceModalVisible(true);
  };

  const handleCopy = async (record) => {
    if (record?.orderTypeId === 10) {
      const body = {
        sourceIntegrationId: record?.id,
        orderProcessType: 5,
      };
      const action = await dispatch(prepareLoadBalance(body));
      if (prepareLoadBalance.fulfilled.match(action)) {
        successDialog({
          title: <Text t="success" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: async () => {
            history.push('/ticket-restaurant/balance-upload');
          },
        });
      }
    }
  };

  const handlePrint = async (record) => {
    const customerId = selectedCustomer?.customerId;

    let data = {
      customerId: customerId,
      orderId: record?.id,
      vouId: 1,
    };

    try {
      const blob = await reportsServices.getOrderReport(data);
      await previewDownload(blob);
    } catch (error) { }
  };

  const handleDownloadModal = (record) => {
    dispatch(setSelectedOrder(record));
    setDownloadModalVisible(true);
  };

  const handlePaymentLink = async (record) => {
    dispatch(setSelectedOrder(record));
    setPaymentModalVisible(true);
  };

  const handleCancel = async (record) => {
    const body = {
      id: record?.id,
      orderTypeId: record?.orderTypeId,
      vouId: record?.vouId,
    };

    confirmDialog({
      title: <Text t="attention" />,
      htmlContent: <Text t="orderCancellationWarning" />,
      onOk: async () => {
        const action = await dispatch(orderDelete(body));
        if (orderDelete.fulfilled.match(action)) {
          successDialog({
            title: <Text t="success" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: async () => {
              await dispatch(orderGetPageList({ ...filterObject }));
            },
          });
        }
      },
    });
  };

  const handleInfo = async (record) => {
    const body = {
      orderId: record?.id,
      vouId: 1,
      orderType: record?.orderTypeId,
    };
    const action = await dispatch(orderInfosGetList(body));
    if (orderInfosGetList.fulfilled.match(action)) {
      const rawMessage = action?.payload?.data?.items[0]?.message || '';
      warningDialog({
        title: <Text t="orderStatus" />,
        message: dictionary[rawMessage] || rawMessage,
      });
    }
  };

  const isShowButton = (item) => {
    if (
      item?.paymentTotalPrice !== 0 &&
      item?.orderCancel === false &&
      item?.paymentResult === false &&
      customerVou.patId === 2 &&
      customerVou.pamId === 4 &&
      item?.isLinkPaymentOk === false
    ) {
      return (
        <>
          {!item?.isLinkPaymentOk ? (
            <CustomButton
              id="ticketRestaurant-myOrders-orderList-order-list-detail-card-footer-payment"
              data-testid="pay"
              onClick={() => {
                return handlePaymentLink(item);
              }}
              className="order-list-detail-card-footer-payment"
              style={{
                margin: isMobil ? '0px' : '0 0 0 40px',
              }}
            >
              <Text t="pay" />
            </CustomButton>
          ) : null}
        </>
      );
    }
  };

  const isPaymentMethod = (item) => {
    if (
      (customerVou.patId === 2 && customerVou.pamId === 5) ||
      (customerVou.patId === 2 && customerVou.pamId === 2)
    ) {
      return 'paymentType.transfer';
    } else if (
      (customerVou.patId === 1 && customerVou.pamId === 1) ||
      (customerVou.patId === 1 && customerVou.pamId === 5)
    ) {
      return 'paymentType.deferred';
    } else if (customerVou.patId === 2 && customerVou.pamId === 4) {
      return 'paymentType.creditCard';
    }
  };

  const handleChangeCollapse = (order) => {
    if (order?.id) {
      const data = {
        orderTypeId: order?.orderTypeId === FilterOrderTypeValue.balanceLoad ? 1 : 0,
        tempOrderNo: order?.id,
      };
      dispatch(getOrderLimitDate(data));
    }
  };

  return (
    <CustomCollapseCard
      switchIcon={true}
      className="order-list-card"
      cardTitle={<Text t="myOrders" />}
      diffPage={true}
    >
      <OrderFilter />
      {list && list.length > 0
        ? list.map((order) => {
          const isOrderBalance =
            order?.orderTypeId === FilterOrderTypeValue.balanceLoad
              ? order?.orderTypeName
              : `${order?.orderTypeName} - ${order?.cardTypeName}`;
          return (
            <Collapse
              className={`order-list-card-new ${activeCollapse ? 'active' : ''}`}
              ghost
              expandIconPosition="right"
              expandIcon={getExpandIcon(setActiveCollapse, false)}
              key={order.id}
              switchIcon={false}
              cardTitle={<Text t="orderTracking" />}
              onChange={() => handleChangeCollapse(order)}
            >
              <Panel
                header={
                  <div
                    style={{
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: isMobil ? '100% !important' : 'auto !important',
                    }}
                  >
                    <div>
                      <p>
                        <Text t="orderNo" />
                      </p>
                      <span>{order?.id}</span>
                    </div>
                    {!isMobil ? (
                      <div>
                        <p>
                          <Text t="orderType" />
                        </p>
                        <span>{isOrderBalance}</span>
                      </div>
                    ) : null}
                    {!isMobil ? (
                      <div>
                        <p>
                          <Text t="orderAmount2" />
                        </p>
                        <span>{order?.totalPrice} TL</span>
                      </div>
                    ) : null}
                    {!isMobil ? (
                      <div>
                        <p>
                          <Text t="orderDate2" />
                        </p>
                        <span>{dayjs(order?.orderDate).format(defaultDateFormat)}</span>
                      </div>
                    ) : null}
                    <div className="mobil">
                      <p
                        style={{
                          color:
                            order?.orderStatusName === 'Krediler Üretildi' ||
                              order?.orderStatusName === 'Teslim Edildi'
                              ? '#00a483'
                              : '#f14c08',
                          fontFamily: 'UbuntuBold',
                        }}
                      >
                        {order?.orderStatusName}
                      </p>
                    </div>
                  </div>
                }
                key="1"
              >
                <div className="">
                  <div
                    className="order-list-detail-card"
                    style={{
                      paddingLeft: isMobil ? '0.5rem' : '1.5rem',
                      paddingRight: isMobil ? '0.5rem' : '2rem',
                    }}
                  >
                    <div className="order-list-detail-card-title">
                      <h2>
                        <Text t="orderSummary2" />
                      </h2>
                    </div>

                    {order?.orderStatusName !== 'Talep Alındı' && (
                      <div
                        className="order-list-detail-card-link"
                        style={{
                          flexDirection: isMobil ? 'column' : 'row',
                          justifyContent: isMobil ? 'center' : 'space-between',
                          alignItems: isMobil ? 'center' : 'end',
                          marginTop: isMobil ? '2.5rem' : '0px',
                          marginBottom: isMobil ? '3.5rem' : '0px',
                        }}
                      >
                        <div>
                          {!handleAuthStatus(userAuthorizationStatus, 'MyOrdersList') && (
                            <button style={{ backgroundColor: 'transparent', borderWidth: 0 }} type="button" data-testid={detailText} onClick={() => handleDetail(order)}>
                              <br />
                              <Text t="viewOrderDetail" />
                            </button>
                          )}
                        </div>
                        <div>
                          {!handleAuthStatus(userAuthorizationStatus, 'MyOrdersDownload') && (
                            <p data-testid="download" onClick={() => handleDownloadModal(order)}>
                              <Text t="downloadOrderDetail" />
                            </p>
                          )}
                        </div>
                        <div>
                          {!handleAuthStatus(userAuthorizationStatus, 'MyOrdersDownload') && (
                            <p data-testid="print" onClick={() => handlePrint(order)}>
                              <Text t="printOrderDetail" />
                            </p>
                          )}
                        </div>
                        <div>
                          {!handleAuthStatus(userAuthorizationStatus, 'MyOrdersCopy') &&
                            order?.orderTypeId !== FilterOrderTypeValue.newCard && (
                              <button style={{ backgroundColor: 'transparent', borderWidth: 0 }} type="button" onClick={() => handleCopy(order)}><Text t="repeat" /></button>
                            )}
                        </div>
                      </div>
                    )}

                    <div
                      className="order-list-detail-card-summary"
                      style={{
                        flexDirection: isMobil ? 'column' : 'row',
                      }}
                    >
                      <div
                        className="order-list-detail-card-summary-first-area"
                        style={{
                          paddingLeft: isMobil ? '0px' : '1rem',
                          minWidth: isMobil ? '100%' : '45%',
                        }}
                      >
                        <div
                          style={{
                            marginTop: isMobil ? '0.5rem' : 0,
                            marginBottom: isMobil ? '0.5rem' : 0,
                          }}
                        >
                          <p
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: 0,
                              paddingTop: isMobil ? 0 : '12px',
                              paddingBottom: isMobil ? '6px' : '12px',
                            }}
                          >
                            <Text t="orderCreatedBy" />
                          </p>
                          <span
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: '0px !important',
                            }}
                          >
                            {order?.createdUser}
                          </span>
                        </div>
                        <div
                          style={{
                            marginTop: isMobil ? '0.5rem' : 0,
                            marginBottom: isMobil ? '0.5rem' : 0,
                          }}
                        >
                          <p
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: 0,
                              paddingTop: isMobil ? 0 : '12px',
                              paddingBottom: isMobil ? '6px' : '12px',
                              width: isMobil ? '38%' : 'auto',
                              lineHeight: isMobil ? '16px' : 'inherit',
                            }}
                          >
                            <Text t="dateWhenBalanceWillBeActive2" />
                          </p>
                          <span
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: '0px !important',
                            }}
                          >
                            {dayjs(order?.activationDate)
                              .format(defaultDateFormat)
                              .replaceAll('.', '/')}
                          </span>
                        </div>

                        <div
                          style={{
                            marginTop: isMobil ? '0.5rem' : 0,
                            marginBottom: isMobil ? '0.5rem' : 0,
                          }}
                        >
                          <div
                            style={{
                              marginBottom: isMobil ? '0.5rem' : 0,
                              paddingTop: isMobil ? 0 : '12px',
                              paddingBottom: isMobil ? 0 : '12px',
                              minWidth: isMobil ? 'auto' : '20%',
                            }}
                          >
                            <p
                              style={{
                                fontSize: isMobil ? '12px' : '14px',
                                marginBottom: 0,
                              }}
                            >
                              <Text t="orderStatus" />:{' '}
                            </p>
                            {order?.orderStatusName !== 'Talep Alındı' && (
                              <CustomImage
                                data-testid="orderStatusName"
                                style={{ cursor: 'pointer', paddingLeft: '0.5rem' }}
                                src={iconInfo}
                                onClick={() => {
                                  return handleInfo(order);
                                }}
                              />
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: '0px !important',
                            }}
                          >
                            {order?.orderStatusName}
                          </span>
                        </div>
                      </div>
                      <div
                        className="order-list-detail-card-summary-second-area"
                        style={{
                          paddingRight: isMobil ? '0px' : '1rem',
                          minWidth: isMobil ? '100%' : '45%',
                        }}
                      >
                        {order.orderTypeId !== 11 && (
                          <div
                            style={{
                              marginBottom: isMobil ? '0.5rem' : 0,
                            }}
                          >
                            <p
                              style={{
                                fontSize: isMobil ? '12px' : '14px',
                                marginBottom: 0,
                                paddingTop: isMobil ? 0 : '12px',
                                paddingBottom: isMobil ? '6px' : '12px',
                              }}
                            >
                              <Text t="amountToBePaid" />{' '}
                            </p>
                            <span
                              style={{
                                fontSize: isMobil ? '12px' : '14px',
                                marginBottom: '0px !important',
                              }}
                            >
                              {order?.paymentTotalPrice} TL
                            </span>
                          </div>
                        )}
                        <div
                          style={{
                            marginTop: isMobil ? '0.5rem' : 0,
                            marginBottom: isMobil ? '0.5rem' : 0,
                          }}
                        >
                          <p
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: 0,
                              paddingTop: isMobil ? 0 : '12px',
                              paddingBottom: isMobil ? '6px' : '12px',
                            }}
                          >
                            <Text t="numberOfCards2" />{' '}
                          </p>
                          <span
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: '0px !important',
                            }}
                          >
                            {order?.totalCount}
                          </span>
                        </div>
                        <div
                          style={{
                            marginTop: isMobil ? '0.5rem' : 0,
                            marginBottom: isMobil ? '0.5rem' : 0,
                          }}
                        >
                          <p
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: 0,
                              paddingTop: isMobil ? 0 : '12px',
                              paddingBottom: isMobil ? '6px' : '12px',
                            }}
                          >
                            <Text t="paymentMethod" />{' '}
                          </p>
                          <span
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: '0px !important',
                            }}
                          >
                            <Text t={isPaymentMethod()} />
                          </span>
                        </div>
                        <div
                          style={{
                            marginTop: isMobil ? '0.5rem' : 0,
                            marginBottom: isMobil ? '0.5rem' : 0,
                            borderBottom: '1px solid transparent',
                          }}
                        >
                          <p
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: 0,
                              paddingTop: isMobil ? 0 : '12px',
                              paddingBottom: isMobil ? '6px' : '12px',
                            }}
                          >
                            <Text t="lastPaymentDate" />
                          </p>
                          <span
                            style={{
                              fontSize: isMobil ? '12px' : '14px',
                              marginBottom: '0px !important',
                            }}
                          >
                            {orderLimitDate?.data?.limitDate !== null
                              ? dayjs(orderLimitDate?.data?.limitDate).format(defaultDateFormat)
                              : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {order?.orderStatusName !== 'Talep Alındı' && (
                      <div
                        className="order-list-detail-card-footer"
                        style={{
                          flexDirection: isMobil ? 'column' : 'row',
                        }}
                      >
                        {!handleAuthStatus(userAuthorizationStatus, 'MyOrdersCancel') && (
                          <>
                            {order?.orderCancel === false && order?.showCancelButton === true && (
                              <CustomButton
                                id="ticketRestaurant-myOrders-orderList-order-list-detail-card-footer-cancel"
                                data-testid="delete"
                                onClick={() => {
                                  return handleCancel(order);
                                }}
                                className="order-list-detail-card-footer-cancel"
                                style={{
                                  margin: isMobil ? '0px' : '0 0 0 40px',
                                }}
                              >
                                <Text t="cancelOrder" />
                              </CustomButton>
                            )}
                          </>
                        )}

                        {isShowButton(order)}
                      </div>
                    )}
                  </div>
                </div>
              </Panel>
            </Collapse>
          );
        })
        : null}
      <div className="order-list-footer">
        <CustomPagination
          current={tableProperty?.currentPage}
          defaultCurrent={1}
          pageSize={tableProperty?.pageSize}
          total={tableProperty?.totalCount}
          onChange={async (pageNumber, pageSize) => {
            await handleTableChange(pageNumber, pageSize);
          }}
        />
      </div>
      <DownloadModal
        modalVisible={downloadModalVisible}
        handleModalVisible={setDownloadModalVisible}
      />
      <PaymentModal
        modalVisible={paymentModalVisible}
        handleModalVisible={setPaymentModalVisible}
        paymentPage={handleAuthStatus(userAuthorizationStatus, 'PaymentAuthorizationGroup')}
      />
      <OrderSummaryCardModal
        modalVisible={orderSummaryCardModalVisible}
        handleModalVisible={setOrderSummaryCardModalVisible}
      />
      <OrderSummaryBalanceModal
        modalVisible={orderSummaryBalanceModalVisible}
        handleModalVisible={setOrderSummaryBalanceModalVisible}
      />
    </CustomCollapseCard>
  );
};

export default OrderList;
